/**
 * t513u: phone Edit details for owners and admins, from the person sheet's Manage list. Same rules and Save as the
 * desktop dialog (features/people/users/EditUser.tsx); Save saves and closes, Cancel just closes.
 */
import { useEffect, useRef } from 'react';
import { BottomSheet } from '../BottomSheet';
import { F, ghostBtn, saveBtn } from '../settings/PersonSettings';
import { useEditDetails } from '@/features/people/users/EditUser';

const inp = 'h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-base dark:border-slate-700 dark:bg-slate-900';

export function EditDetailsSheet({ userId, name, open, onClose }: { userId: string; name: string; open: boolean; onClose: () => void }) {
  const e = useEditDetails(userId, open);
  const { form, setForm } = e;
  const submit = async () => { if (await e.save()) onClose(); };
  // A problem (e.g. the email is already used) can sit below the keyboard or the fold: bring it into view.
  const err = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (e.error) err.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [e.error]);
  return (
    <BottomSheet open={open} onClose={onClose} title={`Edit ${name}`} testId="edit-details-sheet" footer={
      <div className="flex gap-2">
        <button type="button" className={ghostBtn} onClick={onClose} data-testid="edit-user-cancel">Cancel</button>
        <button type="button" className={saveBtn} disabled={!e.p || e.saving} onClick={() => void submit()} data-testid="edit-user-save">{e.saving ? 'Saving…' : 'Save'}</button>
      </div>
    }>
      {!e.p ? <p className="px-4 py-4 text-sm text-slate-500">Loading…</p> : (
        <form className="px-4 pb-2" onSubmit={ev => { ev.preventDefault(); void submit(); }} data-testid="edit-user">
          <F label="Name"><input className={inp} value={form.display_name} maxLength={120} autoComplete="off" onChange={ev => setForm({ ...form, display_name: ev.target.value })} data-testid="edit-user-name" /></F>
          <F label="Email"><input className={inp} type="email" inputMode="email" autoCapitalize="none" value={form.email} maxLength={320} autoComplete="off" onChange={ev => setForm({ ...form, email: ev.target.value })} data-testid="edit-user-email" /></F>
          <p className="pb-1 text-xs text-slate-500 dark:text-slate-400">{e.emailChanging ? 'This is how they sign in. We’ll tell them at their old address, and resend an open invite to the new one.' : 'This is how they sign in.'}</p>
          <F label="Phone"><input className={inp} type="tel" inputMode="tel" value={form.phone} maxLength={40} autoComplete="off" onChange={ev => setForm({ ...form, phone: ev.target.value })} data-testid="edit-user-phone" /></F>
          {e.canTitle && <F label="Job title"><input className={inp} value={form.job_title} maxLength={120} autoComplete="off" onChange={ev => setForm({ ...form, job_title: ev.target.value })} data-testid="edit-user-job-title" /></F>}
          {e.p.can.view_hr && <p className="py-1 text-xs text-slate-500 dark:text-slate-400">Preferred and legal names are in their Personal details.</p>}
          {e.error && <p ref={err} className="py-1 text-sm text-rose-700 dark:text-rose-300" role="alert" data-testid="edit-user-error">{e.error}</p>}
          <button type="submit" hidden />
        </form>
      )}
    </BottomSheet>
  );
}
